"use client";

import { clsx } from "@trydig/ui";
import type React from "react";
import { forwardRef } from "react";
import { Plus } from "~/components/icons/16/Plus";
import { Minus } from "~/components/icons/Minus";
import styles from "./Accordion.module.css";

type AccRootProps = React.ComponentProps<"div"> & {
	ref?: React.Ref<HTMLDivElement>;
};
const Root = forwardRef<HTMLDivElement, AccRootProps>(({ className, children, ...rest }, ref) => {
	return (
		<div ref={ref} className={clsx(className, styles.root)} {...rest}>
			{children}
		</div>
	);
});
Root.displayName = "AccordionRoot";

type AccItemProps = React.ComponentProps<"details"> & {
	ref?: React.Ref<HTMLDetailsElement>;
};
const Item = forwardRef<HTMLDetailsElement, AccItemProps>(
	({ className, children, ...rest }, ref) => {
		return (
			<details ref={ref} className={clsx(className, styles.item)} {...rest}>
				{children}
			</details>
		);
	},
);
Item.displayName = "AccordionItem";

type AccTriggerProps = React.ComponentProps<"summary"> & {
	ref?: React.Ref<HTMLElement>;
};
const Trigger = forwardRef<HTMLElement, AccTriggerProps>(
	({ className, children, ...rest }, ref) => {
		return (
			<summary ref={ref} className={clsx(className, styles.trigger)} {...rest}>
				{children}
				<div className={styles.arrow__down}>
					<Plus />
				</div>
				<div className={styles.arrow__up}>
					<Minus />
				</div>
			</summary>
		);
	},
);
Trigger.displayName = "AccordionTrigger";

type AccContentProps = React.ComponentProps<"div"> & {
	ref?: React.Ref<HTMLDivElement>;
};
const Content = forwardRef<HTMLDivElement, AccContentProps>(
	({ className, children, ...rest }, ref) => {
		return (
			<div ref={ref} className={clsx(className, styles.content)} {...rest}>
				{children}
			</div>
		);
	},
);
Content.displayName = "AccordionContent";

const Accordion = ({
	items,
}: {
	items: {
		title: string;
		content: React.ReactNode;
	}[];
}) => {
	return (
		<Root>
			{items?.map((item, i) => {
				return (
					<Item key={i}>
						<Trigger>{item.title}</Trigger>
						<Content>{item.content}</Content>
					</Item>
				);
			})}
		</Root>
	);
};

export {
	Accordion,
	Content as AccordionContent,
	Content as ClientAccordionContent,
	Item as AccordionItem,
	Item as ClientAccordionItem,
	Root as AccordionRoot,
	Root as ClientAccordionRoot,
	Trigger as AccordionTrigger,
	Trigger as ClientAccordionTrigger,
};
